import { create } from 'zustand';

type MediaType = 'image' | 'video';

interface MediaReadyState {
  projectId?: string;
  expected: Record<MediaType, Set<string>>;
  loaded: Record<MediaType, Set<string>>;
  hasRetryPolling: boolean;
  emitted: boolean;
  registerUrls: (type: MediaType, urls: string[], projectId?: string) => void;
  markLoaded: (type: MediaType, url: string, pollCount?: number) => void;
  consumeIfReady: () => {
    ready: boolean;
    payload?: {
      projectId?: string;
      messageId: string;
      urls: { image: string[]; video: string[] };
      hasRetryPolling: boolean;
      readyTypes: { image: boolean; video: boolean };
    };
  };
  reset: (projectId?: string) => void;
}

const createEmptySets = () => ({ image: new Set<string>(), video: new Set<string>() });

export const useMediaReadyStore = create<MediaReadyState>((set, get) => ({
  projectId: undefined,
  expected: createEmptySets(),
  loaded: createEmptySets(),
  hasRetryPolling: false,
  emitted: false,

  registerUrls: (type, urls, projectId) =>
    set(state => {
      const nextExpected = { ...state.expected };
      const setForType = new Set(nextExpected[type]);
      urls.filter(Boolean).forEach(url => setForType.add(url));
      nextExpected[type] = setForType;
      return {
        projectId: projectId ?? state.projectId,
        expected: nextExpected,
        // 重置 emitted，以防新的批次复用旧状态
        emitted: false,
      };
    }),

  markLoaded: (type, url, pollCount) =>
    set(state => {
      if (!url) {
        return state;
      }
      const nextLoaded = { ...state.loaded };
      const setForType = new Set(nextLoaded[type]);
      setForType.add(url);
      nextLoaded[type] = setForType;
      const hasRetryPolling = state.hasRetryPolling || (pollCount !== undefined && pollCount > 1);
      return {
        loaded: nextLoaded,
        hasRetryPolling,
      };
    }),

  consumeIfReady: () => {
    const state = get();
    if (state.emitted) {
      return { ready: false };
    }

    const expectedImage = state.expected.image.size;
    const expectedVideo = state.expected.video.size;
    const totalExpected = expectedImage + expectedVideo;
    if (totalExpected === 0) {
      return { ready: false };
    }

    const imageReady = expectedImage === 0 || state.loaded.image.size >= expectedImage;
    const videoReady = expectedVideo === 0 || state.loaded.video.size >= expectedVideo;

    if (!(imageReady && videoReady)) {
      return { ready: false };
    }

    const payload = {
      projectId: state.projectId,
      messageId: 'media-ready',
      urls: {
        image: Array.from(state.expected.image),
        video: Array.from(state.expected.video),
      },
      hasRetryPolling: state.hasRetryPolling,
      readyTypes: {
        image: expectedImage > 0,
        video: expectedVideo > 0,
      },
    };

    set({ emitted: true });
    return { ready: true, payload };
  },

  reset: (projectId) =>
    set({
      projectId,
      expected: createEmptySets(),
      loaded: createEmptySets(),
      hasRetryPolling: false,
      emitted: false,
    }),
}));
